/**
 * Authentication Service
 *
 * Provides centralized authentication functionality for the Crisis Buddy frontend.
 * This service handles:
 * - Login/logout flows
 * - Token refresh with retry logic
 * - Session validation via /auth/me endpoint
 * - User state management
 *
 * Architecture:
 * - Tokens are stored in HttpOnly cookies (set by backend)
 * - User data is stored in Zustand store (persisted to localStorage)
 * - This service coordinates between API calls and store updates
 *
 * Token Lifecycle:
 * - Access token: 15 minutes, stored in HttpOnly cookie
 * - Refresh token: 30 days, rotated on each refresh
 * - On 401: ApiService triggers refresh, then retries the request
 */

import { apiService } from './api'
import {
  type AuthState,
  type User,
  type LoginResponse,
  type ProfileResponse,
  AuthStateSchema,
  UserSchema,
  LoginResponseSchema,
} from '../types'
import { AUTH_ENDPOINTS } from '../../api/endpoints'

// Constants for retry logic
const MAX_REFRESH_RETRIES = 3
const BASE_RETRY_DELAY_MS = 1000
const MAX_RETRY_DELAY_MS = 10000

class AuthService {
  private userStorageKey = 'auth_user'
  private refreshPromise: Promise<void> | null = null
  private isLoggingOut = false
  private lastRefreshTime = 0
  private static readonly MIN_REFRESH_INTERVAL_MS = 5000 // Minimum 5 seconds between refresh attempts

  // =========================================================================
  // Public API
  // =========================================================================

  /**
   * Login with email/password and optional 2FA token.
   *
   * Flow:
   * 1. POST /auth/login with credentials
   * 2. If 2FA required, throw error for UI to handle
   * 3. On success, fetch /auth/me to get full RBAC data
   * 4. Store user in localStorage
   *
   * @param email User email
   * @param password User password
   * @param totpToken Optional 2FA token
   * @returns User object with RBAC data
   * @throws Error if login fails or 2FA required
   */
  async login(email: string, password: string, totpToken?: string): Promise<User> {
    const payload: { email: string; password: string; totpToken?: string } = {
      email,
      password,
    }

    if (totpToken) {
      payload.totpToken = totpToken
    }

    const responseRaw = (await apiService.post(AUTH_ENDPOINTS.LOGIN, payload))
    console.debug('[AuthService] Login response:', responseRaw)
    const response = responseRaw as LoginResponse;

    // Check if 2FA setup is required
    if (response.data?.requires_2faSetup) {
      // Don't store user yet - return special object with flag
      const user = response.data.user
      return {
        ...user,
        requires2faSetup: true,
      } as User
    }

    // Check if 2FA is required
    if (response.data?.requires_2fa) {
      throw new Error('auth.twoFactorRequired')
    }

    const loginResponse = LoginResponseSchema.parse(response)
    let { user } = loginResponse.data

    // Fetch full profile with RBAC data
    user = await this.fetchUserWithRbac(user)

    // Store user
    this.setUser(user)

    return user
  }

  /**
   * Register a new user.
   *
   * @param email User email
   * @param password User password
   * @param name Optional display name
   * @returns User object with RBAC data
   */
  async register(email: string, password: string, name?: string): Promise<User> {
    const response = await apiService.post(AUTH_ENDPOINTS.REGISTER, {
      email,
      password,
      name,
    })

    const registerResponse = LoginResponseSchema.parse(response)
    let { user } = registerResponse.data

    // Fetch full profile with RBAC data
    user = await this.fetchUserWithRbac(user)

    // Store user
    this.setUser(user)

    return user
  }

  /**
   * Logout the current user.
   *
   * Clears both backend session (via API) and frontend state.
   */
  async logout(): Promise<void> {
    try {
      await apiService.post(AUTH_ENDPOINTS.LOGOUT, {})
    } catch (error) {
      console.debug('[AuthService] Logout API call failed (may be expected):', error)
    } finally {
      this.clearAllState()
      window.location.href = '/login'
    }
  }

  /**
   * Validate current session by calling /auth/me.
   *
   * Used on app startup to check if the user is still authenticated
   * and to refresh user/RBAC data.
   *
   * @returns User object if authenticated, null otherwise
   */
  async validateSession(): Promise<User | null> {
    try {
      const response = (await apiService.get(AUTH_ENDPOINTS.ME)) as ProfileResponse

      if (response?.data?.user) {
        const user: User = {
          ...response.data.user,
          roles: response.data.roles || [],
          permissions: response.data.permissions || [],
          isSuperAdmin: response.data.isSuperAdmin || false,
        }
        this.setUser(user)
        return user
      }
      return null
    } catch (error) {
      console.debug('[AuthService] Session validation failed:', error)
      return null
    }
  }

  /**
   * Refresh the access token using the refresh token cookie.
   *
   * Features:
   * - Request deduplication (only one refresh at a time)
   * - Retry with exponential backoff
   * - Minimum interval between refresh attempts to prevent rapid-fire calls
   *
   * Called automatically by ApiService on 401 responses.
   */
  async refreshTokenWithDedup(): Promise<void> {
    // If a refresh is already in progress, wait for it
    if (this.refreshPromise) {
      console.debug('[AuthService] Refresh already in progress, waiting for existing promise')
      return this.refreshPromise
    }

    // Check if we've refreshed too recently (debounce rapid calls)
    const now = Date.now()
    const timeSinceLastRefresh = now - this.lastRefreshTime
    if (timeSinceLastRefresh < AuthService.MIN_REFRESH_INTERVAL_MS && this.lastRefreshTime > 0) {
      console.debug(
        `[AuthService] Refresh called too soon (${timeSinceLastRefresh}ms since last), skipping`
      )
      return
    }

    // Create the refresh promise before any async work to prevent race conditions
    this.refreshPromise = this.performRefreshWithRetry()
    this.lastRefreshTime = now

    try {
      await this.refreshPromise
    } finally {
      this.refreshPromise = null
    }
  }

  /**
   * Handle authentication failure.
   *
   * Called when:
   * - Token refresh fails after max retries
   * - Session is determined to be invalid
   *
   * Single point of exit for auth failures to prevent multiple redirects.
   * Once triggered, the isLoggingOut flag stays true until page reload.
   */
  async handleAuthFailure(reason: string): Promise<void> {
    // Prevent multiple concurrent logout attempts
    if (this.isLoggingOut) {
      console.debug('[AuthService] Already logging out, ignoring:', reason)
      return
    }

    this.isLoggingOut = true
    console.warn('[AuthService] Auth failure triggered:', reason)

    try {
      await apiService.post(AUTH_ENDPOINTS.LOGOUT, {})
    } catch (error) {
      console.debug('[AuthService] Logout API call failed (expected):', error)
    }

    this.clearAllState()

    // Single redirect - isLoggingOut stays true to prevent any further logout attempts
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
    // Note: isLoggingOut is NOT reset - page will reload on redirect
  }

  // =========================================================================
  // User Storage
  // =========================================================================

  getUser(): User | null {
    const userJson = localStorage.getItem(this.userStorageKey)
    if (!userJson) return null

    try {
      return UserSchema.parse(JSON.parse(userJson))
    } catch {
      return null
    }
  }

  setUser(user: User): void {
    localStorage.setItem(this.userStorageKey, JSON.stringify(user))
  }

  clearUser(): void {
    localStorage.removeItem(this.userStorageKey)
  }

  /**
   * Check if user appears to be authenticated.
   *
   * Note: This only checks if user data exists locally.
   * The actual token is in an HttpOnly cookie and cannot be verified client-side.
   * Use validateSession() for server-side validation.
   */
  isAuthenticated(): boolean {
    return !!this.getUser()
  }

  /**
   * Get current auth state for store initialization.
   */
  getAuthState(): AuthState {
    return AuthStateSchema.parse({
      isAuthenticated: this.isAuthenticated(),
      user: this.getUser(),
      isLoading: false,
    })
  }

  // =========================================================================
  // Private Helpers
  // =========================================================================

  /**
   * Fetch user profile with RBAC data from /auth/me or /auth/profile.
   */
  private async fetchUserWithRbac(baseUser: User): Promise<User> {
    try {
      const profileResponse = (await apiService.get(AUTH_ENDPOINTS.ME)) as ProfileResponse
      if (profileResponse?.data) {
        return {
          ...baseUser,
          roles: profileResponse.data.roles || [],
          permissions: profileResponse.data.permissions || [],
          isSuperAdmin: profileResponse.data.isSuperAdmin || false,
        }
      }
    } catch (error) {
      console.warn('[AuthService] Failed to load RBAC data:', error)
    }
    return baseUser
  }

  /**
   * Perform token refresh with retry and exponential backoff.
   * On final failure, triggers handleAuthFailure to log user out.
   */
  private async performRefreshWithRetry(): Promise<void> {
    for (let attempt = 0; attempt < MAX_REFRESH_RETRIES; attempt++) {
      try {
        console.debug(`[AuthService] Refresh attempt ${attempt + 1}/${MAX_REFRESH_RETRIES}`)

        await apiService.post(AUTH_ENDPOINTS.REFRESH, {})

        console.debug('[AuthService] ✅ Token refresh successful')
        return
      } catch (error) {
        console.error(
          `[AuthService] Refresh attempt ${attempt + 1}/${MAX_REFRESH_RETRIES} failed:`,
          error
        )

        if (attempt === MAX_REFRESH_RETRIES - 1) {
          // All retries exhausted - trigger auth failure
          console.error('[AuthService] ❌ All refresh attempts failed, logging out')
          await this.handleAuthFailure('Token refresh failed after all retries')
          throw error
        }

        // Calculate backoff delay
        const delay = Math.min(BASE_RETRY_DELAY_MS * Math.pow(2, attempt), MAX_RETRY_DELAY_MS)
        console.debug(`[AuthService] Retrying in ${delay}ms...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  /**
   * Clear all authentication state.
   */
  private clearAllState(): void {
    this.clearUser()
    localStorage.removeItem('auth-storage')
    localStorage.removeItem('crisis-storage')
    sessionStorage.clear()

    // Reset crisis store
    try {
      // Dynamic import to avoid circular dependency
      import('../stores/crisisStore').then(({ useCrisisStore }) => {
        useCrisisStore.getState().reset()
      })
    } catch (error) {
      console.debug('[AuthService] Failed to reset crisis store:', error)
    }
  }
}

export const authService = new AuthService()
